import React, { useState, useEffect } from 'react';
import { Trophy, Calendar, Users, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import EventManagement from './EventManagement';
import { Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

// List of admin UIDs - same as in Admin.js
const ADMIN_UIDS = [
  "iMTsoOPrg6eR4rlJmaL1f0pA07Y2"
  // Add other admin UIDs here as needed
];

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { currentUser, getIdToken } = useAuth();
  
  // Check if current user is admin
  const isAdmin = currentUser && ADMIN_UIDS.includes(currentUser.uid);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/events`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const data = await response.json();
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will remove all entries and cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete event');
      }
      
      // Remove the deleted event from the state
      setEvents(events.filter(event => event.id !== eventId));
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCountryRestriction = (event) => {
    if (!event.country_restriction || event.allowed_countries?.includes('all')) {
      return 'Open to all countries';
    }
    
    const countries = event.allowed_countries || [];
    if (countries.length === 0) {
      return 'Open to all countries';
    } else if (countries.length === 1) {
      return `${countries[0]} only`;
    } else if (countries.length <= 3) {
      return `${countries.join(', ')} only`;
    } else {
      return `${countries.length} countries`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
        <Trophy className="w-8 h-8 mr-3 text-yellow-500" />
        Dance Events
      </h1>

      {/* Add EventManagement component for admin users */}
      {isAdmin && (
        <div className="mb-8">
          <EventManagement getIdToken={getIdToken} />
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map(event => (
          <div key={event.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{event.name}</h3>
              <p className="text-gray-600 mb-4">{event.description}</p>
              
              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Starts: {formatDate(event.start_date)}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Ends: {formatDate(event.end_date)}</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{event.entries_count || 0} entries</span>
                </div>
                {event.country_restriction && (
                  <div className="flex items-center text-yellow-600">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span>{formatCountryRestriction(event)}</span>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-between items-center">
                <span className="text-sm font-medium text-blue-600">Entry Fee: 2 credits</span>
                <div className="flex gap-2">
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent navigation to event details
                        handleDeleteEvent(event.id);
                      }}
                      className="flex items-center px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    View Event
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      

      {events.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Trophy className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Active Events</h3>
          <p className="text-gray-500">Check back later for upcoming dance events!</p>
          {isAdmin && (
            <p className="text-sm text-gray-500 mt-2">
              As an admin, you can create new events using the Event Management panel above.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EventsList;